import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PORTFOLIO_OPTIONS = [
  { value: '1',    labelEn: '1 property',          labelEs: '1 propiedad' },
  { value: '2-5',  labelEn: '2–5 properties',       labelEs: '2–5 propiedades' },
  { value: '6-20', labelEn: '6–20 properties',      labelEs: '6–20 propiedades' },
  { value: '21+',  labelEn: '21+ properties',       labelEs: '21+ propiedades' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RequestDemoModal = ({ open, onOpenChange, source = 'hero' }) => {
  const { language, t } = useLanguage();
  const isEs = language === 'es';
  const tr = (es, en) => (isEs ? es : en);

  const [form, setForm] = useState({
    name: '',
    email: '',
    portfolioSize: '2-5',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState('');

  // Reset state when the modal is reopened
  useEffect(() => {
    if (open) {
      setStatus(null);
      setErrorMsg('');
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      return tr('Ingresa tu nombre completo (mínimo 2 caracteres).',
                 'Enter your full name (min. 2 characters).');
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      return tr('Ingresa un email válido.', 'Enter a valid email address.');
    }
    if (!form.portfolioSize) {
      return tr('Selecciona el tamaño de tu portafolio.',
                 'Select your portfolio size.');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;              // guard against double submits
    const v = validate();
    if (v) { setStatus('error'); setErrorMsg(v); return; }

    setSubmitting(true);
    setStatus(null);
    setErrorMsg('');

    const portfolioLabel = PORTFOLIO_OPTIONS.find((o) => o.value === form.portfolioSize);
    const portfolioText = portfolioLabel
      ? (isEs ? portfolioLabel.labelEs : portfolioLabel.labelEn)
      : form.portfolioSize;

    const subject = isEs
      ? `Solicitud de demo — ${portfolioText}`
      : `Demo Request — ${portfolioText}`;

    const messageLines = isEs
      ? [
          'Nueva solicitud de demo desde el sitio público.',
          '',
          `Nombre: ${form.name.trim()}`,
          `Email: ${form.email.trim()}`,
          `Tamaño de portafolio: ${portfolioText}`,
          `Teléfono / WhatsApp: ${form.phone.trim() || 'no provisto'}`,
          `Origen: ${source}`,
          `Idioma: ${language}`,
        ]
      : [
          'New demo request from the public site.',
          '',
          `Name: ${form.name.trim()}`,
          `Email: ${form.email.trim()}`,
          `Portfolio size: ${portfolioText}`,
          `Phone / WhatsApp: ${form.phone.trim() || 'not provided'}`,
          `Source: ${source}`,
          `Language: ${language}`,
        ];
    const message = messageLines.join('\n');

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject,
          message,
        }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        let detail = '';
        try {
          const data = await res.json();
          if (typeof data?.detail === 'string') detail = data.detail;
        } catch (_) { /* ignore */ }
        setStatus('error');
        setErrorMsg(detail || tr(
          'No pudimos enviar tu solicitud. Intenta de nuevo en unos minutos.',
          'We could not send your request. Please try again in a few minutes.'
        ));
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(tr(
        'Error de conexión. Verifica tu internet e intenta de nuevo.',
        'Connection error. Check your internet and try again.'
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    // On success, also clear the form so a fresh re-open starts clean.
    if (status === 'success') {
      setForm({ name: '', email: '', portfolioSize: '2-5', phone: '' });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="sm:max-w-md bg-white"
        data-testid="request-demo-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-navy-deep">
            {tr('Solicita una demostración', 'Request a Demo')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {tr(
              'Cuéntanos sobre tu portafolio. Te contactamos en menos de 24 horas hábiles.',
              'Tell us about your portfolio. We reply within one business day.'
            )}
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div
            className="flex flex-col items-center text-center py-6"
            data-testid="request-demo-success"
          >
            <CheckCircle2 className="w-12 h-12 text-emerald-rich mb-4" />
            <h3 className="font-playfair text-xl text-navy-deep mb-2">
              {tr('¡Solicitud recibida!', 'Request received!')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              {tr(
                'Gracias por tu interés en VillaPilot.ai. Hemos recibido tu solicitud. Ahora puedes elegir un horario que te convenga para tu demo privada.',
                'Thank you for your interest in VillaPilot.ai. Your request has been received. You can now choose a convenient time for your private demo.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <a
                href="https://calendar.app.google/nhRBPFVmiSPLQwpk6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-10 px-6 rounded-sm bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep text-sm font-semibold transition-colors"
                data-testid="request-demo-schedule-cta"
              >
                {tr('Agenda tu demo', 'Schedule Your Demo')}
                <ExternalLink className="w-4 h-4" />
              </a>
              <Button
                onClick={close}
                variant="ghost"
                className="rounded-sm"
                data-testid="request-demo-close-success"
              >
                {tr('Cerrar', 'Close')}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="request-demo-form">
            <div className="space-y-1">
              <Label htmlFor="rd-name" className="text-xs font-semibold uppercase tracking-wider">
                {tr('Nombre', 'Name')}
              </Label>
              <Input
                id="rd-name"
                data-testid="request-demo-name"
                value={form.name}
                onChange={handleChange('name')}
                placeholder={tr('Jane Doe', 'Jane Doe')}
                autoComplete="name"
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="rd-email" className="text-xs font-semibold uppercase tracking-wider">
                {tr('Email', 'Email')}
              </Label>
              <Input
                id="rd-email"
                type="email"
                data-testid="request-demo-email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder={tr('tu@correo.com', 'you@email.com')}
                autoComplete="email"
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="rd-portfolio" className="text-xs font-semibold uppercase tracking-wider">
                {tr('Tamaño de portafolio', 'Portfolio size')}
              </Label>
              <select
                id="rd-portfolio"
                data-testid="request-demo-portfolio"
                value={form.portfolioSize}
                onChange={handleChange('portfolioSize')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                required
              >
                {PORTFOLIO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {isEs ? o.labelEs : o.labelEn}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="rd-phone" className="text-xs font-semibold uppercase tracking-wider">
                {tr('Teléfono / WhatsApp (opcional)', 'Phone / WhatsApp (optional)')}
              </Label>
              <Input
                id="rd-phone"
                type="tel"
                data-testid="request-demo-phone"
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="+1 555 123 4567"
                autoComplete="tel"
              />
            </div>

            {status === 'error' && errorMsg && (
              <div
                className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                data-testid="request-demo-error"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                disabled={submitting}
                data-testid="request-demo-cancel"
              >
                {tr('Cancelar', 'Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep rounded-sm font-semibold"
                data-testid="request-demo-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {tr('Enviando…', 'Sending…')}
                  </>
                ) : (
                  tr('Enviar solicitud', 'Send request')
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RequestDemoModal;
